import { useLanguage } from '../LanguageContext';
import React, { useState, useEffect, useCallback } from "react";
import DataTable from './DataTable';
import axiosInstance from "../axiosInstance";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";

const AdminDashboard = () => {
  const { t } = useLanguage();
  // User creation state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("moderator");
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarType, setSnackbarType] = useState("success");

  // User list state
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState("");

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/api/get_users", { withCredentials: true });
      setUsers(response.data.users || []);
    } catch (err) {
      console.error("❌ Error fetching users:", err);
      showSnackbar(t("Failed to fetch users."), "error");
    } finally {
      setIsLoading(false);
    }
  }, [t]); // ✅ Empty dependency array ensures `fetchUsers` does not change
  
  useEffect(() => {
    fetchUsers();
    checkCurrentUserRole();
  }, [fetchUsers]); // ✅ Now safe to include

  // ✅ Check logged-in user role
  const checkCurrentUserRole = async () => {
    try {
      const response = await axiosInstance.get("/api/check-auth", { withCredentials: true });
      setCurrentUserRole(response.data.role);
    } catch (err) {
      console.error("❌ Error checking user role:", err);
    }
  };

  // ✅ Create a new user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post("/api/register", { username, password, role }, { withCredentials: true });
      showSnackbar(t("User {name} created successfully!", { name: username }), "success");

      // ✅ Reset form
      setUsername("");
      setPassword("");
      setRole("moderator");

      // ✅ Close the modal
      setCreateUserOpen(false);

      fetchUsers(); // Refresh user list
    } catch (err) {
      console.error("❌ Error creating user:", err);
      showSnackbar(t("Failed to create user."), "error");
    }
  };

  // ✅ Open edit modal
  const handleEditUser = (user) => {
    setEditUser(user);
  };

  // ✅ Save edited user
  const handleSaveEdit = async () => {
    try {
      await axiosInstance.post(
        "/api/edit_user",
        {
          user_id: editUser.id,
          username: editUser.username,
          password: editUser.password || "", // Allow optional password reset
          role: editUser.role, // Role change allowed only for superadmin
        },
        { withCredentials: true }
      );

      showSnackbar(t("User {name} updated successfully!", { name: editUser.username }), "success");

      // ✅ Close modal
      setEditUser(null);

      // ✅ Refresh user list
      fetchUsers();
    } catch (err) {
      console.error("❌ Error updating user:", err);
      showSnackbar(t("Failed to update user."), "error");
    }
  };

  const handleDeleteUser = async (userId, username, role) => {
    if (currentUserRole !== "superadmin" && role === "admin") {
      showSnackbar(t("❌ You are not allowed to delete Admin users."), "error");
      return;
    }

    if (currentUserRole === "admin" && role !== "moderator") {
      showSnackbar(t("❌ Admins can only delete Moderators."), "error");
      return;
    }

    if (!window.confirm(t("Are you sure you want to delete {name}?", { name: username }))) {
      return; // ✅ Cancel deletion if user clicks "No"
    }

    try {
      await axiosInstance.delete(`/api/delete_user/${userId}`, { withCredentials: true });
      showSnackbar(t("✅ User {name} deleted successfully!", { name: username }), "success");
      fetchUsers(); // Refresh user list after deletion
    } catch (err) {
      console.error("❌ Error deleting user:", err);
      showSnackbar(t("Failed to delete user."), "error");
    }
  };

  // ✅ Snackbar handler
  const showSnackbar = (message, type) => {
    setSuccessMessage(message);
    setSnackbarType(type);
    setSnackbarOpen(true);
  };

  return (
    <div className="container">
      <h1>{t("Admin Dashboard")}</h1>

      {/* ✅ Button to Open Create User Modal */}
      <Button variant="contained" color="primary" onClick={() => setCreateUserOpen(true)}>{t("Create User")}</Button>

      <hr />

      {/* ✅ User List */}
      <h2>{t("Existing Users")}</h2>
      <DataTable label="users" rows={users} loading={isLoading} rowKey={user => user.id} columns={[
        { key: 'username', label: t("Username") },
        { key: 'id', label: t("User ID"), mono: true },
        { key: 'role', label: t("Role"), value: user => t(({ moderator: 'Moderator', admin: 'Admin', superadmin: 'Superadmin' })[user.role] || user.role) },
        { key: 'actions', label: t("Actions"), searchable: false, render: user => <div className="row-actions">
                {currentUserRole === "superadmin" || (currentUserRole === "admin" && user.role === "moderator") ? (
                  <>
                    <button onClick={() => handleEditUser(user)}>{t("Edit")}</button>
                    <button
                      onClick={() => handleDeleteUser(user.id, user.username, user.role)}
                      style={{ marginLeft: "10px", color: "red" }}
                    >{t("Delete")}</button>
                  </>
                ) : (
                  t("No actions")
                )}
        </div> },
      ]} />

      {/* ✅ Create User Modal */}
      <Dialog open={createUserOpen} onClose={() => setCreateUserOpen(false)}>
        <DialogTitle>{t("Create New User")}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("Username")}
            fullWidth
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="dense"
            required
          />
          <TextField
            label={t("Password")}
            fullWidth
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="dense"
            required
          />
          <Select
            fullWidth
            value={role}
            onChange={(e) => setRole(e.target.value)}
            margin="dense"
          >
            <MenuItem value="moderator">{t("Moderator")}</MenuItem>
            <MenuItem value="admin">{t("Admin")}</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUserOpen(false)} color="secondary">{t("Cancel")}</Button>
          <Button onClick={handleCreateUser} color="primary">{t("Create")}</Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Edit User Modal */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)}>
        <DialogTitle>{t("Edit User")}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("Username")}
            fullWidth
            value={editUser?.username || ""}
            onChange={(e) => setEditUser({ ...editUser, username: e.target.value })}
            margin="dense"
          />
          <TextField
            label={t("New Password (optional)")}
            fullWidth
            type="password"
            placeholder={t("Leave empty to keep current password")}
            onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
            margin="dense"
          />
          {/* ✅ Only Superadmin can modify roles */}
          {currentUserRole === "superadmin" ? (
            <Select
              fullWidth
              value={editUser?.role || "moderator"}
              onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
              margin="dense"
            >
              <MenuItem value="moderator">{t("Moderator")}</MenuItem>
              <MenuItem value="admin">{t("Admin")}</MenuItem>
            </Select>
          ) : (
            <TextField
              fullWidth
              label={t("Role")}
              value={t(({ moderator: "Moderator", admin: "Admin", superadmin: "Superadmin" })[editUser?.role] || editUser?.role)}
              margin="dense"
              disabled
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)} color="secondary">{t("Cancel")}</Button>
          <Button onClick={handleSaveEdit} color="primary">{t("Save")}</Button>
        </DialogActions>
      </Dialog>


      {/* ✅ Snackbar Notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarType}>
          {t(successMessage)}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AdminDashboard;
